/**
 * Per-user UI state — DESIGN.md §6.1, §7.2, §12.2.
 *
 * Device preferences (sidebar width, text size) live in the browser; this is
 * the other kind: state a person expects to *follow* them. "The state of each
 * section should be remembered for each user so unused sections stay collapsed
 * when not used" is a statement about the person, not the laptop — collapsing
 * Icebox on the desktop and finding it open on the laptop would be a bug.
 *
 * Deliberately narrow. This is not a general key/value bag: every field is
 * declared here, so what the server stores per user stays reviewable and
 * bounded.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * A section, as this state refers to one: its uuid, or `none:<projectId>` for
 * the project's implicit "(No section)" bucket, which has no row of its own.
 */
export const sectionStateKeySchema = z.union([
    uuidSchema,
    z
        .string()
        .regex(/^none:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Expected none:<projectId>'),
]);
/** The key a section is remembered under. */
export function sectionStateKey(projectId, sectionId) {
    return sectionId ?? `none:${projectId}`;
}
export const uiStateSchema = z
    .object({
    /**
     * Sections this user keeps collapsed. Only collapsed ones are stored, so
     * the row stays small: expanding is the absence of a key, and a deleted
     * section's key is harmless noise that the UI never asks about again.
     */
    collapsedSections: z.array(z.string()),
})
    .strict();
/**
 * A toggle, not a snapshot. Two tabs collapsing two different sections must
 * both survive; sending the whole set would let the slower tab erase the
 * other's change.
 */
export const updateUiStateRequestSchema = z
    .object({
    collapseSections: z.array(sectionStateKeySchema).max(500).default([]),
    expandSections: z.array(sectionStateKeySchema).max(500).default([]),
})
    .strict();
//# sourceMappingURL=ui-state.js.map